/**
 * DigiTech Cloud AI — Human imagery library
 *
 * The catalog of "Brand Shoot Slots" lives here. Every stock image on the
 * public site is tagged with a slot_id. Admins can override any slot from the
 * CMS ("Brand Images" page) at runtime without any code change.
 *
 * URLs point to the Unsplash CDN with sizing/format params, treated with
 * a dark gradient overlay and blue-tinted filter at the component level.
 *
 * Consumers should use the useBrandImage(slotId) hook from
 * lib/BrandImagesContext.jsx instead of importing these constants directly,
 * so overrides take effect live.
 */

const U = (id, w = 1200, q = 75) =>
    `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=${q}`;

// ---------------------------------------------------------------------------
// Default URLs (fallbacks when no CMS override exists)
// ---------------------------------------------------------------------------
export const HERO_BACKDROP = U('1522071820081-009f0129c71c', 1800, 70);
export const TEAM_WIDE_BANNER = U('1600880292203-757bb62b4baf', 1800, 75);
export const OFFICE_NIGHT = U('1553877522-43269d4ea984', 1600, 75);

export const IMG_ENGINEERING = U('1573164713988-8665fc963095', 1200);
export const IMG_AI          = U('1573496359142-b8d87734a5a2', 1200);
export const IMG_ENTERPRISE  = U('1552664730-d307ca884978',   1200);
export const IMG_CLOUD       = U('1573165231977-3f0e27806045', 1200);
export const IMG_SECURITY    = U('1518770660439-4636190af475', 1200);
export const IMG_GOVTECH     = U('1519389950473-47ba0277781c', 1200);
export const IMG_IOT         = U('1518709268805-4e9042af9f23', 1200);
export const IMG_ANALYST     = U('1581091226825-a6a2a5aee158', 1200);
export const IMG_DEVOPS      = U('1580894732444-8ecded7900cd', 1200);

export const IMG_HERO_SPLIT  = U('1531482615713-2afd69097998', 1000);
export const IMG_CTA_BAND    = U('1521737604893-d14cc237f11d', 1800, 70);

export const IMG_MISSION_HERO       = U('1552664730-d307ca884978', 1800, 75);
export const IMG_VALUE_EXCELLENCE   = U('1517048676732-d65bc937f952', 900);
export const IMG_VALUE_INNOVATION   = U('1581091226825-a6a2a5aee158', 900);
export const IMG_VALUE_PARTNERSHIP  = U('1522071820081-009f0129c71c', 900);
export const IMG_VALUE_LEADERSHIP   = U('1573497019940-1c28c88b4f3e', 900);

// Blog topic covers — used when a post has no cover_image (or as tag fallback)
export const BLOG_TOPIC_COVERS = {
    'AI':                       U('1677442136019-21780ecad995', 1200),
    'ARTIFICIAL INTELLIGENCE':  U('1677442136019-21780ecad995', 1200),
    'CLOUD':                    U('1573165231977-3f0e27806045', 1200),
    'CYBERSECURITY':            U('1518770660439-4636190af475', 1200),
    'SECURITY':                 U('1518770660439-4636190af475', 1200),
    'DATA':                     U('1551288049-bebda4e38f71',   1200),
    'ENGINEERING':              U('1573164713988-8665fc963095', 1200),
    'DEVOPS':                   U('1580894732444-8ecded7900cd', 1200),
    'GOVTECH':                  U('1519389950473-47ba0277781c', 1200),
    'AFRICA':                   U('1451187580459-43490279c0fa', 1200),
    'STRATEGY':                 U('1552664730-d307ca884978',   1200),
    'TRANSFORMATION':           U('1600880292203-757bb62b4baf', 1200),
    'IOT':                      U('1518709268805-4e9042af9f23', 1200),
    'DEFAULT':                  U('1518770660439-4636190af475', 1200),
};

// ---------------------------------------------------------------------------
// SLOT CATALOG — every stock image on the site is here, with a stable ID
// so real brand photography can replace it from the CMS without code changes.
// Grouped by page so the admin UI can render them logically.
// ---------------------------------------------------------------------------
export const IMAGE_SLOTS = [
    // Home
    { id: 'home.hero.backdrop',       page: 'Home',     label: 'Hero backdrop',                description: 'Faint people-in-tech silhouette behind the 3D hero.',                default: HERO_BACKDROP },

    // Services (top banner + one per service)
    { id: 'services.banner',          page: 'Services', label: 'Page banner',                  description: 'Wide team photo behind the services page hero.',                    default: TEAM_WIDE_BANNER },
    { id: 'service.card.engineering', page: 'Services', label: 'Card · Software Engineering',  description: 'Header photo for the Advanced Software Engineering card.',           default: IMG_ENGINEERING },
    { id: 'service.card.ai',          page: 'Services', label: 'Card · AI & Data',             description: 'Header photo for the AI & Data Intelligence card.',                  default: IMG_AI },
    { id: 'service.card.enterprise',  page: 'Services', label: 'Card · Enterprise Platforms',  description: 'Header photo for the Enterprise Platforms card.',                    default: IMG_ENTERPRISE },
    { id: 'service.card.cloud',       page: 'Services', label: 'Card · Cloud & DevOps',        description: 'Header photo for the Cloud & DevOps card.',                          default: IMG_CLOUD },
    { id: 'service.card.security',    page: 'Services', label: 'Card · Cybersecurity',         description: 'Header photo for the Cybersecurity & Privacy card.',                 default: IMG_SECURITY },
    { id: 'service.card.govtech',     page: 'Services', label: 'Card · GovTech',               description: 'Header photo for the GovTech & Digital Transformation card.',        default: IMG_GOVTECH },
    { id: 'service.card.connected',   page: 'Services', label: 'Card · Connected Systems',     description: 'Header photo for the Connected Systems / IoT card.',                 default: IMG_IOT },

    // Mission & Vision
    { id: 'mission.hero',             page: 'Mission',  label: 'Hero backdrop',                description: 'Wide team-in-discussion photo behind the mission statement.',        default: IMG_MISSION_HERO },
    { id: 'mission.value.excellence', page: 'Mission',  label: 'Value · Engineering Excellence',description: 'Photo card for the Engineering Excellence value.',                  default: IMG_VALUE_EXCELLENCE },
    { id: 'mission.value.innovation', page: 'Mission',  label: 'Value · Responsible Innovation',description: 'Photo card for the Responsible Innovation value.',                  default: IMG_VALUE_INNOVATION },
    { id: 'mission.value.partnership',page: 'Mission',  label: 'Value · Strategic Partnership', description: 'Photo card for the Strategic Partnership value.',                   default: IMG_VALUE_PARTNERSHIP },

    // Blog (topic fallbacks when a post has no cover)
    { id: 'blog.topic.ai',            page: 'Blog',     label: 'Topic cover · AI',             description: 'Fallback cover for posts tagged AI / Artificial Intelligence.',      default: BLOG_TOPIC_COVERS.AI },
    { id: 'blog.topic.cloud',         page: 'Blog',     label: 'Topic cover · Cloud',          description: 'Fallback cover for posts tagged Cloud.',                             default: BLOG_TOPIC_COVERS.CLOUD },
    { id: 'blog.topic.security',      page: 'Blog',     label: 'Topic cover · Security',       description: 'Fallback cover for posts tagged Security / Cybersecurity.',          default: BLOG_TOPIC_COVERS.SECURITY },
    { id: 'blog.topic.data',          page: 'Blog',     label: 'Topic cover · Data',           description: 'Fallback cover for posts tagged Data / Analytics.',                  default: BLOG_TOPIC_COVERS.DATA },
    { id: 'blog.topic.africa',        page: 'Blog',     label: 'Topic cover · Africa',         description: 'Fallback cover for posts tagged Africa.',                            default: BLOG_TOPIC_COVERS.AFRICA },
    { id: 'blog.topic.engineering',   page: 'Blog',     label: 'Topic cover · Engineering',    description: 'Fallback cover for posts tagged Engineering.',                       default: BLOG_TOPIC_COVERS.ENGINEERING },
    { id: 'blog.topic.devops',        page: 'Blog',     label: 'Topic cover · DevOps',         description: 'Fallback cover for posts tagged DevOps / Reliability.',              default: BLOG_TOPIC_COVERS.DEVOPS },
    { id: 'blog.topic.govtech',       page: 'Blog',     label: 'Topic cover · GovTech',        description: 'Fallback cover for posts tagged GovTech / Public Sector.',           default: BLOG_TOPIC_COVERS.GOVTECH },
    { id: 'blog.topic.default',       page: 'Blog',     label: 'Topic cover · Default',        description: 'Universal fallback cover when no tag matches.',                      default: BLOG_TOPIC_COVERS.DEFAULT },

    // Contact
    { id: 'contact.office',           page: 'Contact',  label: 'Support / office photo',       description: 'Human context photo in the left column of the contact page.',       default: OFFICE_NIGHT },
];

// Quick lookup helpers keyed by slot_id
export const SLOT_DEFAULTS = Object.freeze(
    IMAGE_SLOTS.reduce((acc, s) => { acc[s.id] = s.default; return acc; }, {})
);

// Blog: map a post to a cover slot ID based on its first known tag
export function coverSlotForPost(post) {
    const tags = (post?.tags || []).map((t) => (t || '').toString().toUpperCase());
    const tagToSlot = {
        AI:                       'blog.topic.ai',
        'ARTIFICIAL INTELLIGENCE':'blog.topic.ai',
        CLOUD:                    'blog.topic.cloud',
        CYBERSECURITY:            'blog.topic.security',
        SECURITY:                 'blog.topic.security',
        DATA:                     'blog.topic.data',
        AFRICA:                   'blog.topic.africa',
        ENGINEERING:              'blog.topic.engineering',
        DEVOPS:                   'blog.topic.devops',
        GOVTECH:                  'blog.topic.govtech',
    };
    for (const t of tags) {
        if (tagToSlot[t]) return tagToSlot[t];
    }
    return 'blog.topic.default';
}

// Legacy helper — kept in case some callers still use it directly
export function coverForPost(post) {
    if (post?.cover_image) return post.cover_image;
    const tags = (post?.tags || []).map((t) => (t || '').toString().toUpperCase());
    for (const t of tags) {
        if (BLOG_TOPIC_COVERS[t]) return BLOG_TOPIC_COVERS[t];
    }
    return BLOG_TOPIC_COVERS.DEFAULT;
}

// ---------------------------------------------------------------------------
// Portrait avatars (illustrative team representation — small, decorative;
// intentionally NOT part of the brand-slot system to keep the CMS focused
// on hero-level imagery).
// ---------------------------------------------------------------------------
const AV = (id) => U(id, 200, 75);
export const TEAM_AVATARS = [
    AV('1494790108377-be9c29b29330'),
    AV('1531123897727-8f129e1688ce'),
    AV('1544005313-94ddf0286df2'),
    AV('1519085360753-af0119f7cbe7'),
    AV('1580489944761-15a19d654956'),
    AV('1500648767791-00dcc994a43e'),
];

export function avatarForAuthor(name = '') {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
    return TEAM_AVATARS[hash % TEAM_AVATARS.length];
}

// Convenience map for legacy imports
export const SERVICE_IMAGES = {
    engineering: IMG_ENGINEERING,
    ai:          IMG_AI,
    enterprise:  IMG_ENTERPRISE,
    cloud:       IMG_CLOUD,
    security:    IMG_SECURITY,
    govtech:     IMG_GOVTECH,
    connected:   IMG_IOT,
    devops:      IMG_DEVOPS,
    data:        IMG_ANALYST,
};

export function imageForServiceKey(key) {
    return SERVICE_IMAGES[key] || IMG_ENGINEERING;
}

export function slotForServiceKey(key) {
    return `service.card.${key}`;
}
